import { useState, useEffect, useCallback, useMemo } from 'react';
import DateInput from './DateInput';
import { supabase } from '../lib/supabase';
import { getVendorCategory, resolveVendor, loadAllVendorsForUI } from '../lib/vendors';
import InvoiceScanner from './InvoiceScanner';
import InvoiceReviewPage from './InvoiceReviewPage';
import ExpenseInvoiceReviewPage from './ExpenseInvoiceReviewPage';
import DailySales from './DailySales';
import WasteLog from './WasteLog';
import WeeklyReport from './WeeklyReport';
import { parseLocalDate } from '../utils/dates';
import { matchTokens } from '../utils/search';

const DOW_LABELS=['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

function fmtSchedule(def){
  if(!def)return'';
  if(def.frequency==='one_time')return'One-time ('+(def.scheduled_date||'—')+')';
  if(def.frequency==='daily')return'Daily';
  if(def.frequency==='weekly')return'Weekly ('+((def.days_of_week||[]).map(d=>DOW_LABELS[d]).join(', ')||'—')+')';
  if(def.frequency==='monthly')return'Monthly (day '+(def.day_of_month||1)+')';
  if(def.frequency==='yearly')return'Yearly ('+(def.month_and_day||'')+')';
  return def.frequency||'';
}

export default function Tasks({t,si,sb,sh,sd,mn,orgId,uid,isMobile,refreshBell,setPg,setReviewTaskId,setSel,setCostCardReviewTask,session,userRole,ings,setIngs,settings,save,salesDate,setSalesDate,salesData,setSalesData,salesMeta,setSalesMeta,toastEnabled,weatherByDate,setWeatherByDate,todayS,cashReceived,setCashReceived,setOtherExp,mi,batches,bc,setPurchases,setDuplicateData,setInvoiceModalId,setInvoiceHighlightItem,todayWC,todayW,wasteLog,setWasteLog,wSearch,setWSearch,wQty,setWQty,wReason,setWReason,wSel,setWSel,wErr,setWErr,wFilt,addW,calcWCost,laborByDate,setLaborByDate,wasteItems,setReturnTo,salesByDate,wholesaleByDate,purchases,otherExp,invTotal,prevInv,invCounts,avgCP,fxTot,setPurchasesInitialRange}){
const[scannerOpen,setScannerOpen]=useState(false);
const[gmailConnected,setGmailConnected]=useState(false);
const[scanningGmail,setScanningGmail]=useState(false);
const[scanToast,setScanToast]=useState(null);
const canScanGmail=(userRole==='owner'||userRole==='admin')&&gmailConnected;

useEffect(()=>{
  if(!orgId||!session?.access_token||!(userRole==='owner'||userRole==='admin'))return;
  fetch(`/api/gmail/status?org_id=${orgId}`,{headers:{'Authorization':`Bearer ${session.access_token}`}})
    .then(r=>r.ok?r.json():null).then(d=>{if(d?.connected)setGmailConnected(true);}).catch(()=>{});
},[orgId,session?.access_token,userRole]);

async function scanGmail(){
  if(scanningGmail||!session?.access_token)return;
  setScanningGmail(true);setScanToast(null);
  try{
    const r=await fetch('/api/scan-gmail',{method:'POST',headers:{'Authorization':`Bearer ${session.access_token}`,'Content-Type':'application/json'},body:JSON.stringify({org_id:orgId})});
    const d=await r.json();
    if(!r.ok)throw new Error(d.error||'Scan failed');
    setScanToast(`Scan complete — ${d.invoices_created} new invoice${d.invoices_created!==1?'s':''}`);
    if(d.invoices_created>0)loadTasks();
    setTimeout(()=>setScanToast(null),4000);
  }catch(e){setScanToast(e.message);setTimeout(()=>setScanToast(null),5000);}
  setScanningGmail(false);
}
const[tasks,setTasks]=useState([]);
const[loading,setLoading]=useState(true);
const[view,setView]=useState('active'); // 'active' | 'history'
const[adding,setAdding]=useState(false);
const[rmTitle,setRmTitle]=useState('');
const[rmDate,setRmDate]=useState(()=>new Date().toLocaleDateString('en-CA',{timeZone:'America/New_York'}));
const[rmNote,setRmNote]=useState('');
const[rmRepeat,setRmRepeat]=useState('none');
const[rmVendor,setRmVendor]=useState('');
const[rmVendorOpen,setRmVendorOpen]=useState(false);
const[rmSaving,setRmSaving]=useState(false);
const[allVendors,setAllVendors]=useState([]);
useEffect(()=>{if(orgId)loadAllVendorsForUI(orgId).then(setAllVendors);},[orgId]);
const[recurDefs,setRecurDefs]=useState([]);
const[showRecurring,setShowRecurring]=useState(false);
const[userMap,setUserMap]=useState({}); // {userId: displayName}
const[expandedForm,setExpandedForm]=useState(null); // task id with form open
const[formValues,setFormValues]=useState({});
const[formErrors,setFormErrors]=useState({});
const[formSaving,setFormSaving]=useState(false);

const loadTasks=useCallback(async()=>{
  if(!orgId)return;
  const[{data,error},{data:defs}]=await Promise.all([
    supabase.from('tasks').select('*').eq('org_id',orgId).order('created_at',{ascending:false}),
    supabase.from('recurring_task_definitions').select('*').eq('org_id',orgId).order('created_at',{ascending:false})
  ]);
  if(error)console.error('[Tasks] Load error:',error);
  else setTasks(data||[]);
  setRecurDefs(defs||[]);
  // Load user names for completed_by display
  try{
    const{data:members}=await supabase.rpc('get_org_members_full',{p_org_id:orgId});
    if(members?.length){
      const map={};
      members.forEach(m=>{map[m.user_id]=(m.email||'').split('@')[0]||m.user_id.slice(0,8);});
      setUserMap(map);
    }
  }catch(e){
    // Fallback: try simpler RPC
    try{
      const{data:members}=await supabase.rpc('get_org_members_with_email',{p_org_id:orgId});
      if(members?.length){
        const map={};
        members.forEach(m=>{map[m.user_id]=(m.email||'').split('@')[0]||m.user_id.slice(0,8);});
        setUserMap(map);
      }
    }catch(e2){}
  }
  setLoading(false);
},[orgId]);

useEffect(()=>{loadTasks();},[loadTasks]);

async function saveReminder(){
  if(!rmTitle.trim()||!rmDate||rmSaving)return;
  setRmSaving(true);
  const dueAt=parseLocalDate(rmDate);
  let defId=null;

  // Create recurring definition if repeat is set
  if(rmRepeat!=='none'){
    const d=parseLocalDate(rmDate);
    const cols={frequency:rmRepeat,spawn_time:'06:00',overdue_mode:'never'};
    if(rmRepeat==='weekly')cols.days_of_week=[d.getDay()];
    if(rmRepeat==='monthly')cols.day_of_month=d.getDate();
    if(rmRepeat==='yearly')cols.month_and_day=String(d.getMonth()+1).padStart(2,'0')+'-'+String(d.getDate()).padStart(2,'0');
    const{data:def,error:defErr}=await supabase.from('recurring_task_definitions').insert({
      org_id:orgId,type:'manual_reminder',title:rmTitle.trim(),note:rmNote.trim()||null,
      target_page:null,payload_template:null,
      is_active:true,created_by:uid,...cols
    }).select().single();
    if(defErr){console.error('[Tasks] Save recurring def error:',defErr);setRmSaving(false);return;}
    defId=def.id;
    setRecurDefs(p=>[def,...p]);
  }

  const{data,error}=await supabase.from('tasks').insert({
    org_id:orgId,type:'manual_reminder',status:'pending',
    title:rmTitle.trim(),note:rmNote.trim()||null,
    source:rmRepeat!=='none'?'recurring':'manual',
    due_at:dueAt.toISOString(),priority:0,payload:rmVendor?{vendor:rmVendor}:null,
    recurring_def_id:defId
  }).select().single();
  if(error){console.error('[Tasks] Save reminder error:',error);setRmSaving(false);return;}
  await supabase.from('task_events').insert({org_id:orgId,task_id:data.id,event_type:'created',new_value:'pending',actor_id:uid});
  // Update last_spawned_at on the definition
  if(defId)await supabase.from('recurring_task_definitions').update({last_spawned_at:new Date().toISOString()}).eq('id',defId);
  setTasks(p=>[data,...p]);
  setAdding(false);setRmTitle('');setRmNote('');setRmRepeat('none');setRmVendor('');setRmSaving(false);
  if(refreshBell)refreshBell();
}

async function completeTask(id){
  setJustCompleted(id);
  const now=new Date().toISOString();
  const tk=tasks.find(x=>x.id===id);
  const{error}=await supabase.from('tasks').update({status:'completed',completed_at:now,completed_by:uid}).eq('id',id);
  if(error){console.error('[Tasks] Complete error:',error);return;}
  await supabase.from('task_events').insert({org_id:orgId,task_id:id,event_type:'status_changed',old_value:'pending',new_value:'completed',actor_id:uid});
  const newTasks=tasks.map(x=>x.id===id?{...x,status:'completed',completed_at:now,completed_by:uid}:x);
  // Recurring spawn is handled by the daily cron (api/spawn-recurring.js).
  // No client-side spawn — next instance appears on its scheduled day.
  setTasks(newTasks);
  if(refreshBell)refreshBell();
  // Auto-advance to next task
  selectNextAfter(id);
}

async function deleteTask(id){
  const{error}=await supabase.from('tasks').delete().eq('id',id);
  if(error){console.error('[Tasks] Delete error:',error);return;}
  setTasks(p=>p.filter(tk=>tk.id!==id));
  if(refreshBell)refreshBell();
}

async function toggleRecurDef(defId,active){
  await supabase.from('recurring_task_definitions').update({is_active:!active}).eq('id',defId);
  setRecurDefs(p=>p.map(d=>d.id===defId?{...d,is_active:!active}:d));
}
async function deleteRecurDef(defId){
  await supabase.from('recurring_task_definitions').delete().eq('id',defId);
  setRecurDefs(p=>p.filter(d=>d.id!==defId));
}

function hasForm(tk){return tk.payload?.form_fields?.length>0;}
function hasPage(tk){return tk.target_page&&!REVIEW_TYPES.has(tk.type)&&!hasForm(tk);}
function isNavigable(tk){return REVIEW_TYPES.has(tk.type)||hasPage(tk);}
const PROMPT_PAGES=new Set([]);
function needsPrompt(tk){return hasPage(tk)&&PROMPT_PAGES.has(tk.target_page);}
const[promptTask,setPromptTask]=useState(null);
const PROMPT_TEXT={waste:"All waste entered for today?",weeklyreport:"Done reviewing this week's numbers?"};

async function routeInvoiceReview(tk){
  // Duplicate review: route to App-level DuplicateReviewPage
  if(tk.target_page==='duplicate-review'){
    if(setReviewTaskId)setReviewTaskId(tk.id);
    if(setPg)setPg('duplicate-review');
    return;
  }
  // Look up vendor category to decide which review component
  const stagingId=tk.payload?.staging_id||tk.target_id;
  let vendorName=null;
  if(stagingId){
    const{data:st}=await supabase.from('invoice_staging').select('vendor').eq('id',stagingId).single();
    if(st)vendorName=st.vendor;
  }
  let isExpense=false;
  if(vendorName){
    const cat=await getVendorCategory(orgId,vendorName);
    if(cat==='expense')isExpense=true;
  }
  setActiveIsExpense(isExpense);
  setActiveTaskId(tk.id);
}

function toggleForm(tk){
  if(expandedForm===tk.id){setExpandedForm(null);return;}
  const fields=tk.payload.form_fields;
  const vals={};
  fields.forEach((f,i)=>{vals[i]='';});
  setFormValues(vals);setFormErrors({});
  setExpandedForm(tk.id);
}

async function submitForm(tk){
  const fields=tk.payload.form_fields;
  // Validate required fields
  const errs={};
  fields.forEach((f,i)=>{if(f.required&&!String(formValues[i]||'').trim())errs[i]=true;});
  if(Object.keys(errs).length){setFormErrors(errs);return;}
  setFormSaving(true);
  // Build form_data object
  const formData={};
  fields.forEach((f,i)=>{formData[f.label]=formValues[i]||'';});
  // Save form data to task payload
  const updatedPayload={...(tk.payload||{}),form_data:formData};
  await supabase.from('tasks').update({payload:updatedPayload}).eq('id',tk.id);
  // Update local state so completeTask sees it
  setTasks(p=>p.map(x=>x.id===tk.id?{...x,payload:updatedPayload}:x));
  setExpandedForm(null);setFormSaving(false);
  // Complete the task (handles recurring spawn too)
  completeTask(tk.id);
}

const today=new Date().toLocaleDateString('en-US',{weekday:'long',month:'long',day:'numeric',timeZone:'America/New_York'});
const nowET=new Date(new Date().toLocaleString('en-US',{timeZone:'America/New_York'}));
const todayStart=new Date(nowET.getFullYear(),nowET.getMonth(),nowET.getDate());
const todayEnd=new Date(todayStart);todayEnd.setDate(todayEnd.getDate()+1);

// Split tasks
const pending=tasks.filter(tk=>tk.status==='pending'||tk.status==='in_progress');
const completedToday=tasks.filter(tk=>tk.status==='completed'&&tk.completed_at&&new Date(tk.completed_at)>=todayStart&&new Date(tk.completed_at)<todayEnd);
const overdue=pending.filter(tk=>tk.overdue_at&&new Date(tk.overdue_at)<nowET);
const notOverdue=pending.filter(tk=>!tk.overdue_at||new Date(tk.overdue_at)>=nowET);

// Group non-overdue by type
function typeGroup(type){
  if(type==='invoice_review'||type==='duplicate_invoice')return'Invoices';
  if(type==='invoice_anomaly')return'Invoices';
  if(type==='cost_card_review')return'Cost Cards';
  if(type==='manual_reminder')return'Reminders';
  return'Recurring';
}
const groups={};
notOverdue.forEach(tk=>{const g=typeGroup(tk.type);if(!groups[g])groups[g]=[];groups[g].push(tk);});
const groupOrder=['Invoices','Recurring','Cost Cards','Reminders'];
const sortedGroups=groupOrder.filter(g=>groups[g]?.length);

// Task types that require a review flow — cannot be checked off directly
const REVIEW_TYPES=new Set(['invoice_review','duplicate_invoice','cost_card_review']);

// Type colors
function typeColor(type){
  if(type==='invoice_review'||type==='duplicate_invoice')return t.bl;
  if(type==='invoice_anomaly')return t.ac;
  if(type==='cost_card_review')return'#8B5CF6'; // purple
  if(type==='manual_reminder')return t.td;
  return t.gn; // recurring = green
}

// Progress
const progressX=completedToday.length;
const progressY=progressX+pending.length;
const progressPct=progressY>0?Math.round((progressX/progressY)*100):0;

// Streak: count consecutive days backward where all due-that-day tasks were completed
const streak=(()=>{
  const completed=tasks.filter(tk=>tk.status==='completed'&&tk.completed_at);
  let days=0;
  const d=new Date(nowET);d.setDate(d.getDate()-1); // start from yesterday (today is still in progress)
  for(let i=0;i<365;i++){
    const ds=`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
    const dueThisDay=tasks.filter(tk=>tk.due_at&&tk.due_at.startsWith(ds));
    if(dueThisDay.length===0){d.setDate(d.getDate()-1);continue;} // no tasks due = streak continues
    const allDone=dueThisDay.every(tk=>tk.status==='completed');
    if(!allDone)break;
    days++;d.setDate(d.getDate()-1);
  }
  // Check if today is also clear (bonus: include today if all due-today are done)
  const todayStr=`${nowET.getFullYear()}-${String(nowET.getMonth()+1).padStart(2,'0')}-${String(nowET.getDate()).padStart(2,'0')}`;
  const dueToday=tasks.filter(tk=>tk.due_at&&tk.due_at.startsWith(todayStr));
  if(dueToday.length>0&&dueToday.every(tk=>tk.status==='completed'))days++;
  return days;
})();

// Completion animation
const[justCompleted,setJustCompleted]=useState(null);
const[activeTaskId,setActiveTaskId]=useState(null);
const activeTask=tasks.find(tk=>tk.id===activeTaskId)||null;
const[activeIsExpense,setActiveIsExpense]=useState(false);
const[vendorDetail,setVendorDetail]=useState(null);
const[vendorLoading,setVendorLoading]=useState(false);
useEffect(()=>{
if(activeTask?.type==='manual_reminder'&&activeTask?.payload?.vendor&&orgId){
setVendorLoading(true);setVendorDetail(null);
loadAllVendorsForUI(orgId).then(vendors=>{const v=vendors.find(x=>x.name===activeTask.payload.vendor);setVendorDetail(v||null);setVendorLoading(false);});
}else{setVendorDetail(null);}
},[activeTaskId]);
const closePaneAndRefresh=useCallback(()=>{setActiveTaskId(null);loadTasks();},[loadTasks]);
const panePg=useCallback((target)=>{if(target==='tasks')closePaneAndRefresh();else setPg(target);},[closePaneAndRefresh,setPg]);

// Flattened render list (anomaly tasks expanded to one per anomaly)
const renderItems=useMemo(()=>{const items=[];
for(const tk of [...overdue,...notOverdue]){
if(tk.type==='invoice_anomaly'&&tk.payload?.anomalies?.length){tk.payload.anomalies.forEach((a,i)=>items.push({tk,anomaly:a,key:tk.id+'-'+i}));}
else{items.push({tk,anomaly:null,key:tk.id});}}
return items;},[overdue,notOverdue]);

// Select a task by its object (handles invoice_review async routing)
async function selectTask(tk){if(!tk)return;if(tk.type==='invoice_review'){await routeInvoiceReview(tk);}else{if(tk.due_at&&tk.target_page){const taskDate=tk.due_at.split('T')[0];if(taskDate)setSalesDate(taskDate);}setActiveTaskId(tk.id);}}

// Auto-select first task on load
useEffect(()=>{if(!loading&&!activeTaskId&&renderItems.length>0){const first=renderItems.find(r=>!r.anomaly);if(first)selectTask(first.tk);}},[loading,renderItems.length]);

// Auto-advance: select next pending task after the given id
function selectNextAfter(completedId){
const idx=renderItems.findIndex(r=>r.tk.id===completedId);
const next=renderItems.find((r,i)=>i>idx&&r.tk.status!=='completed'&&!r.anomaly);
if(next)selectTask(next.tk);else setActiveTaskId(null);
}

// History
const history=tasks.filter(tk=>tk.status==='completed'||tk.status==='cancelled');
function getActionLabel(tk){
  if(tk.status==='cancelled'){if(tk.type==='invoice_review')return'Invoice skipped';if(tk.type==='duplicate_invoice')return'Duplicate skipped';return'Cancelled';}
  if(tk.type==='invoice_review')return'Invoice saved';if(tk.type==='duplicate_invoice')return'Duplicate resolved';if(tk.type==='invoice_anomaly')return'Anomaly reviewed';if(tk.type==='cost_card_review')return'Cost card updated';if(tk.type==='manual_reminder')return'Reminder completed';
  if(tk.recurring_def_id&&tk.payload?.target_page){const p=tk.payload.target_page;if(p==='sales')return'Sales entered';if(p==='waste')return'Waste logged';if(p==='dashboard')return'Labor entered';if(p==='inventory')return'Count saved';if(p==='weeklyreport')return'Report reviewed';return'Logged';}
  return'Completed';
}
const[histSearch,setHistSearch]=useState('');
const[histFilter,setHistFilter]=useState(null);
const[expandedTaskId,setExpandedTaskId]=useState(null);
const[expandedStaging,setExpandedStaging]=useState(null);
const[expandedStagingLoading,setExpandedStagingLoading]=useState(false);
function isClickable(tk){
  if(tk.type==='duplicate_invoice')return false;
  if(tk.type==='invoice_review')return true;
  if(tk.type==='invoice_anomaly')return true;
  if(tk.payload?.form_data)return true;
  if(tk.note)return true;
  return false;
}
useEffect(()=>{
  if(!expandedTaskId){setExpandedStaging(null);return;}
  const tk=tasks.find(x=>x.id===expandedTaskId);
  if(!tk||tk.type!=='invoice_review'){setExpandedStaging(null);return;}
  // Skip fetch if snapshot exists on the task payload
  if(tk.payload?.invoice_snapshot){setExpandedStaging(null);setExpandedStagingLoading(false);return;}
  const stagingId=tk.payload?.staging_id||tk.target_id;
  if(!stagingId){setExpandedStaging(null);return;}
  setExpandedStagingLoading(true);
  supabase.from('invoice_staging').select('*').eq('id',stagingId).single().then(({data})=>{
    setExpandedStaging(data||null);setExpandedStagingLoading(false);
  }).catch(()=>{setExpandedStaging(null);setExpandedStagingLoading(false);});
},[expandedTaskId]);
function histChipKey(tk){
  if(tk.type==='invoice_review'||tk.type==='duplicate_invoice')return'_invoices';
  if(tk.type==='invoice_anomaly')return'_anomalies';
  if(tk.type==='cost_card_review')return'_costcards';
  if(tk.type==='manual_reminder')return'_reminders';
  if(tk.payload?.form_fields&&tk.recurring_def_id)return'def_'+tk.recurring_def_id;
  if(tk.recurring_def_id)return'def_'+tk.recurring_def_id;
  return'_type_'+(tk.type||'other');
}
function histChipLabel(tk){
  if(tk.type==='invoice_review'||tk.type==='duplicate_invoice')return'Invoices';
  if(tk.type==='invoice_anomaly')return'Anomalies';
  if(tk.type==='cost_card_review')return'Cost Cards';
  if(tk.type==='manual_reminder')return'Reminders';
  return tk.title;
}
const histChips=(()=>{const map=new Map();history.forEach(tk=>{const k=histChipKey(tk);if(!map.has(k))map.set(k,{key:k,label:histChipLabel(tk),count:0});map.get(k).count++;});return[...map.values()];})();
const filteredHistory=(()=>{let list=histSearch.trim()?history.filter(tk=>matchTokens(tk.title,histSearch)):history;if(histFilter)list=list.filter(tk=>histChipKey(tk)===histFilter);return list;})();

if(loading)return <div style={{textAlign:'center',padding:40,color:t.td}}>Loading tasks...</div>;

return(<div style={{display:'flex',flexDirection:isMobile?'column':'row',height:isMobile?undefined:'calc(100vh - 48px)',margin:isMobile?'-16px':'-24px -32px'}}>
{/* LEFT PANE — work content or history */}
<div style={{flex:1,minWidth:isMobile?0:300,overflow:'auto',padding:isMobile?'16px':'24px 32px'}}>
{view==='history'?<div>
<h1 style={{fontSize:22,fontWeight:700,margin:'0 0 16px'}}>Task History</h1>
<div style={{position:'relative',marginBottom:14}}>
<span style={{position:'absolute',left:10,top:'50%',transform:'translateY(-50%)',fontSize:14,color:t.td,pointerEvents:'none'}}>⌕</span>
<input value={histSearch} onChange={e=>setHistSearch(e.target.value)} placeholder="Search completed tasks..." style={{...si,width:'100%',paddingLeft:32,paddingRight:32,boxSizing:'border-box'}}/>
{histSearch&&<button onClick={()=>setHistSearch('')} style={{position:'absolute',right:8,top:'50%',transform:'translateY(-50%)',background:'none',border:'none',color:t.tm,fontSize:14,cursor:'pointer',padding:'2px 4px'}}>✕</button>}
</div>
{histChips.length>1&&<div style={{display:'flex',flexWrap:'wrap',gap:6,marginBottom:12}}>
<button onClick={()=>setHistFilter(null)} style={{padding:'6px 10px',borderRadius:16,fontSize:11,cursor:'pointer',border:`1px solid ${!histFilter?t.bl:t.bd}`,background:!histFilter?t.bl:t.sf,color:!histFilter?'#fff':t.tm,fontFamily:"'DM Sans',sans-serif"}}>All ({history.length})</button>
{histChips.map(c=><button key={c.key} onClick={()=>setHistFilter(histFilter===c.key?null:c.key)} style={{padding:'6px 10px',borderRadius:16,fontSize:11,cursor:'pointer',border:`1px solid ${histFilter===c.key?t.bl:t.bd}`,background:histFilter===c.key?t.bl:t.sf,color:histFilter===c.key?'#fff':t.tm,fontFamily:"'DM Sans',sans-serif"}}>{c.label} ({c.count})</button>)}
</div>}
{filteredHistory.length===0?<div style={{textAlign:'center',padding:40,color:t.td,fontSize:12}}>No completed tasks{histSearch?' matching "'+histSearch+'"':histFilter?' in this category':' yet'}.</div>:
<div>{(()=>{const sorted=filteredHistory.sort((a,b)=>new Date(b.completed_at||b.created_at)-new Date(a.completed_at||a.created_at));
const groups=new Map();sorted.forEach(tk=>{const w=tk.completed_at||tk.created_at;const d=new Date(w).toLocaleDateString('en-CA',{timeZone:'America/New_York'});if(!groups.has(d))groups.set(d,[]);groups.get(d).push(tk);});
const out=[];let first=true;groups.forEach((tasks,dateKey)=>{
const hdr=parseLocalDate(dateKey).toLocaleDateString('en-US',{weekday:'long',month:'long',day:'numeric',year:'numeric',timeZone:'America/New_York'});
out.push(<div key={'hdr_'+dateKey} style={{fontSize:12,fontWeight:600,color:t.tm,paddingTop:first?0:16,paddingBottom:6,borderBottom:`1px solid ${t.bd}`,marginBottom:4}}>{hdr}</div>);
first=false;
tasks.forEach(tk=>{
const who=tk.completed_by&&userMap[tk.completed_by]?userMap[tk.completed_by]:null;
const when=tk.completed_at||tk.created_at;
const dateFmt=new Date(when).toLocaleDateString('en-US',{month:'short',day:'numeric',timeZone:'America/New_York'});
const timeFmt=tk.completed_at?new Date(tk.completed_at).toLocaleTimeString('en-US',{hour:'numeric',minute:'2-digit',timeZone:'America/New_York'}):'';
const isCancelled=tk.status==='cancelled';
const clickable=isClickable(tk);
const isExpanded=expandedTaskId===tk.id;
const rowClick=clickable?()=>setExpandedTaskId(prev=>prev===tk.id?null:tk.id):undefined;
const panel=isExpanded?<div key={'exp_'+tk.id} style={{background:t.sf,border:`1px solid ${t.bd}`,borderRadius:6,padding:'12px 16px',marginTop:4,marginBottom:8}}>
<div style={{fontSize:11,fontWeight:500,color:t.td}}>Completed: {new Date(when).toLocaleDateString('en-US',{weekday:'long',month:'long',day:'numeric',year:'numeric',timeZone:'America/New_York'})} at {new Date(when).toLocaleTimeString('en-US',{hour:'numeric',minute:'2-digit',timeZone:'America/New_York'})}</div>
{who&&<div style={{fontSize:11,fontWeight:500,color:t.td,marginTop:2}}>By: {who}</div>}
{tk.note&&<div style={{fontSize:12,color:t.tx,marginTop:4}}>Note: {tk.note}</div>}
{tk.type==='invoice_review'&&(()=>{const snap=tk.payload?.invoice_snapshot;const src=snap||expandedStaging;return<div style={{marginTop:8}}>{!snap&&expandedStagingLoading?<div style={{fontSize:11,color:t.td}}>Loading...</div>:src?<div>
<div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:4,marginBottom:6}}>{[['Vendor',src.vendor],['Invoice #',src.invoice_number||'—'],['Date',src.invoice_date||'—'],['Total','$'+(src.grand_total||0).toFixed(2)]].map(([l,v])=><div key={l}><span style={{fontSize:11,fontWeight:500,color:t.td}}>{l}: </span><span style={{fontSize:12,color:t.tx}}>{v}</span></div>)}</div>
{isCancelled?<div style={{fontSize:11,color:t.td,fontStyle:'italic',marginTop:4}}>Rejected — no purchase record created</div>:<button onClick={()=>setPg('purchases')} style={{...sb,background:t.bl,fontSize:10,marginTop:4}}>View in Purchases</button>}
</div>:<div style={{fontSize:11,color:t.td,fontStyle:'italic'}}>Invoice details no longer available</div>}</div>;})()}
{tk.type==='invoice_anomaly'&&tk.payload?.anomalies&&<div style={{marginTop:8}}>
<div style={{fontSize:13,fontWeight:600,marginBottom:2}}>Anomalies on {tk.payload.vendor||'vendor'} invoice</div>
{tk.payload.anomalies.map((a,i)=>{const badgeStyle={fontSize:9,fontWeight:600,padding:'2px 6px',borderRadius:4,whiteSpace:'nowrap'};let badge,detail;if(a.type==='price_change'){const up=a.new_value>a.old_value;badge=<span style={{...badgeStyle,background:up?'rgba(200,55,45,0.12)':'rgba(45,184,122,0.12)',color:up?t.ac:t.gn}}>Price {up?'↑':'↓'}</span>;detail=<span>${(a.old_value||0).toFixed(2)} → ${(a.new_value||0).toFixed(2)}</span>;}else{badge=<span style={{...badgeStyle,background:'rgba(139,143,158,0.12)',color:t.td}}>{a.type}</span>;detail=<span>{a.description||'—'}</span>;}return<div key={i} style={{display:'flex',alignItems:'center',gap:8,padding:'4px 0',borderBottom:i<tk.payload.anomalies.length-1?`1px solid ${t.bd}`:'none'}}>{badge}<span style={{fontSize:11}}>{detail}</span></div>;})}
</div>}
{tk.payload?.form_data&&<div style={{marginTop:8}}>{(tk.payload.form_fields||Object.keys(tk.payload.form_data).map(k=>({label:k}))).map(f=><div key={f.label} style={{marginBottom:4}}><span style={{fontSize:11,fontWeight:500,color:t.td}}>{f.label}: </span><span style={{fontSize:12,color:t.tx}}>{tk.payload.form_data[f.label]||''}</span></div>)}</div>}
</div>:null;
out.push(<div key={tk.id}>
<div style={{display:'grid',gridTemplateColumns:'1fr 100px 140px 80px',alignItems:'center',gap:8,padding:'10px 0',borderBottom:isExpanded?'none':`1px solid ${t.bd}`,cursor:clickable?'pointer':undefined}} onClick={rowClick}>
<div style={{display:'flex',alignItems:'center',gap:8,minWidth:0}}>
<div style={{width:8,height:8,borderRadius:'50%',background:isCancelled?t.ac:t.gn,flexShrink:0}}/>
<div style={{minWidth:0}}>
<div style={{fontSize:12,fontWeight:500,color:t.tm,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{tk.title}</div>
<div style={{fontSize:10,color:t.td,marginTop:1}}>{getActionLabel(tk)}</div>
</div>
</div>
<div style={{fontSize:11,color:t.td}}>{who||'—'}</div>
<div style={{fontSize:11,color:t.tm}}>{dateFmt}{timeFmt?' · '+timeFmt:''}</div>
<div><span style={{fontSize:9,padding:'2px 8px',borderRadius:4,background:isCancelled?'rgba(200,55,45,0.1)':'rgba(45,184,122,0.1)',color:isCancelled?t.ac:t.gn,fontWeight:500}}>{isCancelled?'cancelled':'completed'}</span></div>
</div>
{panel}
</div>);});});return out;})()}</div>}
</div>
:!activeTask?<div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100%'}}><div style={{color:t.td,fontSize:14}}>Select a task to get started</div></div>
:activeTask.type==='invoice_review'&&!activeIsExpense?<InvoiceReviewPage taskId={activeTask.id} orgId={orgId} uid={uid} ings={ings} setIngs={setIngs} mi={mi} batches={batches} bc={bc} settings={settings} save={save} onConfirm={(entry)=>{if(entry&&setPurchases)setPurchases(p=>[...p,entry]);closePaneAndRefresh();}} setOtherExp={setOtherExp} t={t} si={si} sb={sb} sh={sh} sd={sd} mn={mn} setPg={panePg} refreshBell={refreshBell} setReviewTaskId={setReviewTaskId}/>
:activeTask.type==='invoice_review'&&activeIsExpense?<ExpenseInvoiceReviewPage taskId={activeTask.id} orgId={orgId} uid={uid} settings={settings} setOtherExp={setOtherExp} t={t} si={si} sb={sb} sh={sh} sd={sd} mn={mn} setPg={panePg} refreshBell={refreshBell}/>
:activeTask.type==='cost_card_review'?<div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100%'}}><div style={{color:t.td,fontSize:14}}>Cost card review — coming in next phase</div></div>
:activeTask.target_page==='sales'?<div>
<DailySales salesDate={salesDate} setSalesDate={setSalesDate} salesData={salesData} setSalesData={setSalesData} salesMeta={salesMeta} setSalesMeta={setSalesMeta} toastEnabled={toastEnabled} weatherByDate={weatherByDate} setWeatherByDate={setWeatherByDate} todayS={todayS} t={t} si={si} sb={sb} mn={mn} isMobile={false} cashReceived={cashReceived} setCashReceived={setCashReceived} save={save} orgId={orgId} uid={uid} settings={settings} session={session}/>
<div style={{marginTop:16,display:'flex',justifyContent:'center'}}><button onClick={()=>{completeTask(activeTask.id);setActiveTaskId(null);}} style={{...sb,background:t.gn,minWidth:200,padding:12,fontSize:13}}>Save & complete</button></div>
</div>
:activeTask.target_page==='waste'?<WasteLog salesDate={activeTask.due_at?activeTask.due_at.split('T')[0]:salesDate} setSalesDate={setSalesDate} t={t} si={si} sb={sb} sh={sh} sd={sd} mn={mn} todayWC={todayWC} todayW={todayW} wasteLog={wasteLog} setWasteLog={setWasteLog} wSearch={wSearch} setWSearch={setWSearch} wQty={wQty} setWQty={setWQty} wReason={wReason} setWReason={setWReason} wSel={wSel} setWSel={setWSel} wErr={wErr} setWErr={setWErr} wFilt={wFilt} addW={addW} calcWCost={calcWCost} settings={settings} todayS={todayS} save={save} isMobile={false} laborByDate={laborByDate} orgId={orgId} uid={uid} wasteItems={wasteItems} ings={ings} batches={batches} mi={mi} setPg={panePg} setReturnTo={setReturnTo} onComplete={()=>{completeTask(activeTask.id);setActiveTaskId(null);}}/>
:activeTask.target_page==='dashboard'?<div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100%'}}>
<div style={{textAlign:'center'}}>
<div style={{fontSize:22,fontWeight:700,marginBottom:4}}>Daily Labor</div>
<div style={{fontSize:13,color:t.td,marginBottom:24}}>{parseLocalDate(salesDate).toLocaleDateString('en-US',{weekday:'long',month:'long',day:'numeric'})}</div>
<div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:6,marginBottom:24}}>
<span style={{fontSize:20,color:t.td}}>$</span>
<input value={laborByDate[salesDate]?.labor||''} onChange={e=>{const v=e.target.value.replace(/[^0-9.]/g,'');setLaborByDate(p=>({...p,[salesDate]:{...p[salesDate],labor:v}}));}} placeholder="0" style={{...si,fontSize:20,fontFamily:mn,width:200,textAlign:'center',padding:'10px 12px'}}/>
</div>
<button onClick={()=>{completeTask(activeTask.id);setActiveTaskId(null);}} style={{...sb,background:t.gn,minWidth:200,padding:12,fontSize:13}}>Day complete</button>
</div>
</div>
:activeTask.target_page==='weeklyreport'?<div>
<WeeklyReport salesDate={salesDate} salesByDate={salesByDate} wholesaleByDate={wholesaleByDate} laborByDate={laborByDate} wasteLog={wasteLog} purchases={purchases} otherExp={otherExp} ings={ings} invTotal={invTotal} prevInv={prevInv} invCounts={invCounts} settings={settings} avgCP={avgCP} fxTot={fxTot} t={t} mn={mn} calcWCost={calcWCost} setPg={panePg} orgId={orgId} setPurchasesInitialRange={setPurchasesInitialRange}/>
<div style={{marginTop:16,display:'flex',justifyContent:'center'}}><button onClick={()=>{completeTask(activeTask.id);setActiveTaskId(null);}} style={{...sb,background:t.gn,minWidth:200,padding:12,fontSize:13}}>Week complete</button></div>
</div>
:activeTask.target_page==='inventory'?<div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100%'}}>
<div style={{textAlign:'center'}}>
<div style={{fontSize:22,fontWeight:700,marginBottom:4}}>Inventory Count</div>
<div style={{fontSize:13,color:t.td,marginBottom:24}}>{parseLocalDate(salesDate).toLocaleDateString('en-US',{weekday:'long',month:'long',day:'numeric'})}</div>
<div style={{background:t.sf,borderRadius:12,padding:'20px 32px',marginBottom:12}}>
<div style={{fontSize:10,color:t.tm,textTransform:'uppercase',marginBottom:6}}>On Hand Value</div>
<div style={{fontFamily:mn,fontSize:28,fontWeight:700,color:t.gn}}>${invTotal.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</div>
</div>
<div style={{fontSize:11,color:t.td,marginBottom:24}}>Updates live as you enter counts on the Inventory page</div>
<button onClick={()=>{completeTask(activeTask.id);setActiveTaskId(null);}} style={{...sb,background:t.gn,minWidth:200,padding:12,fontSize:13}}>Inventory complete</button>
</div>
</div>
:activeTask.type==='manual_reminder'&&activeTask.payload?.vendor?<div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100%'}}>
{vendorLoading?<div style={{color:t.td,fontSize:14}}>Loading...</div>:<div style={{textAlign:'center'}}>
<div style={{fontSize:22,fontWeight:700,marginBottom:4}}>{activeTask.payload.vendor}</div>
{vendorDetail?.contact_name&&<div style={{fontSize:14,color:t.tm,marginBottom:4}}>{vendorDetail.contact_name}</div>}
{vendorDetail?.contact_phone&&<div style={{marginBottom:4}}><a href={`tel:${vendorDetail.contact_phone}`} style={{fontSize:16,color:t.bl,textDecoration:'none',fontFamily:mn}}>{vendorDetail.contact_phone}</a></div>}
{vendorDetail?.contact_email&&<div style={{marginBottom:16}}><a href={`mailto:${vendorDetail.contact_email}`} style={{fontSize:13,color:t.bl,textDecoration:'none'}}>{vendorDetail.contact_email}</a></div>}
{!vendorDetail?.contact_phone&&!vendorDetail?.contact_email&&<div style={{fontSize:12,color:t.td,marginBottom:16}}>No contact info on file</div>}
<div style={{background:t.sf,borderRadius:10,padding:'16px 24px',marginBottom:16,textAlign:'left',maxWidth:400,margin:'0 auto 16px'}}>
<div style={{fontSize:14,fontWeight:600,marginBottom:4}}>{activeTask.title}</div>
{activeTask.note&&<div style={{fontSize:12,color:t.td}}>{activeTask.note}</div>}
{activeTask.due_at&&<div style={{fontSize:11,color:t.td,marginTop:4}}>Due {new Date(activeTask.due_at).toLocaleDateString('en-US',{weekday:'short',month:'short',day:'numeric',timeZone:'America/New_York'})}</div>}
</div>
<button onClick={()=>{completeTask(activeTask.id);setActiveTaskId(null);}} style={{...sb,background:t.gn,minWidth:200,padding:12,fontSize:13}}>Done</button>
</div>}
</div>
:activeTask.target_page?<div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100%'}}><div style={{color:t.td,fontSize:14}}>Navigate to {activeTask.target_page} to complete this task</div></div>
:<div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100%'}}><div style={{color:t.td,fontSize:14}}>Select a task to get started</div></div>}
</div>
{/* RIGHT RAIL — task queue */}
<div style={{width:isMobile?'100%':280,minWidth:isMobile?0:220,flexShrink:isMobile?0:1,borderLeft:isMobile?'none':`1px solid ${t.bd}`,borderTop:isMobile?`1px solid ${t.bd}`:'none',overflow:'auto',padding:16,display:'flex',flexDirection:'column'}}>
<style>{`@keyframes taskComplete{0%{transform:scale(1);opacity:1}50%{transform:scale(1.02);opacity:0.7}100%{transform:scale(0.95)translateX(20px);opacity:0}}@keyframes spin{to{transform:rotate(360deg)}}`}</style>
{scanToast&&<div style={{background:t.cd,border:`1px solid ${t.bd}`,borderRadius:8,padding:'8px 14px',marginBottom:8,fontSize:11,color:t.tm}}>{scanToast}</div>}

{/* Rail header */}
<div style={{marginBottom:12}}>
<div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
<div style={{fontSize:14,fontWeight:500}}>Tasks</div>
{streak>0&&<div style={{display:'flex',alignItems:'center',gap:4,background:'rgba(212,168,36,0.12)',borderRadius:8,padding:'3px 10px'}}>
<span style={{fontSize:12}}>🔥</span>
<span style={{fontFamily:mn,fontSize:12,fontWeight:600,color:t.yl}}>{streak} days</span>
</div>}
</div>
<div style={{fontSize:11,color:t.td,marginTop:2}}>{today}</div>
</div>

{/* Progress bar */}
<div style={{background:t.sf,borderRadius:10,padding:'10px 12px',marginBottom:12}}>
<div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:6}}>
<span style={{fontSize:10,color:t.td}}>Today's progress</span>
<span style={{fontFamily:mn,fontSize:12,fontWeight:600,color:progressPct===100&&progressY>0?t.gn:t.bl}}>{progressX}/{progressY}</span>
</div>
<div style={{width:'100%',height:6,background:t.bd,borderRadius:99,overflow:'hidden'}}>
<div style={{width:`${progressPct}%`,height:'100%',background:progressPct===100?t.gn:`linear-gradient(to right,${t.ac},#1a56db)`,borderRadius:99,transition:'width 0.5s ease'}}/>
</div>
</div>

{/* Action buttons */}
<div style={{display:'flex',gap:6,marginBottom:12}}>
<button onClick={()=>setScannerOpen(p=>!p)} style={{...sb,flex:1,background:scannerOpen?t.tm:t.bl,fontSize:10,padding:'8px 0'}}>{scannerOpen?'Close':'⬆ Invoice scanner'}</button>
{canScanGmail&&<button onClick={scanGmail} disabled={scanningGmail} style={{...sb,flex:1,background:t.bl,fontSize:10,padding:'8px 0'}}>{scanningGmail?<><span style={{display:'inline-block',width:10,height:10,border:'2px solid rgba(255,255,255,0.3)',borderTopColor:'#fff',borderRadius:'50%',animation:'spin 0.6s linear infinite',marginRight:4,verticalAlign:'middle'}}/>...</>:'✉ Scan email'}</button>}
</div>

<div style={{borderBottom:`1px solid ${t.bd}`,marginBottom:12}}/>

{/* Invoice Scanner — collapsible */}
{scannerOpen&&<InvoiceScanner t={t} si={si} sb={sb} sh={sh} sd={sd} mn={mn} orgId={orgId} uid={uid} ings={ings} setIngs={setIngs} onConfirm={(entry)=>{if(entry&&setPurchases)setPurchases(p=>[...p,entry]);loadTasks();}} settings={settings} save={save} salesDate={salesDate} setOtherExp={setOtherExp} refreshBell={refreshBell} mi={mi} batches={batches} bc={bc} setPg={setPg} setDuplicateData={setDuplicateData}/>}

{/* View tabs + add button */}
<div style={{display:'flex',gap:6,marginBottom:12,alignItems:'center'}}>
<button onClick={()=>setView('active')} style={{...sb,background:view==='active'?t.bl:t.sf,color:view==='active'?'#fff':t.tm,fontSize:10,padding:'5px 12px'}}>Active</button>
<button onClick={()=>setView('history')} style={{...sb,background:view==='history'?t.bl:t.sf,color:view==='history'?'#fff':t.tm,fontSize:10,padding:'5px 12px'}}>History</button>
<div style={{flex:1}}/>
<button onClick={()=>{setAdding(!adding);if(!adding){setRmTitle('');setRmNote('');setRmRepeat('none');setRmVendor('');setRmDate(new Date().toLocaleDateString('en-CA',{timeZone:'America/New_York'}));}}} style={{...sb,background:adding?t.tm:t.ac,fontSize:10,padding:'5px 10px'}}>{adding?'Cancel':'+ Reminder'}</button>
</div>

{/* Inline reminder form */}
{adding&&<div style={{background:t.cd,borderRadius:12,border:`1px solid ${t.bd}`,padding:16,marginBottom:16}}>
<div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'end'}}>
<div style={{flex:2,minWidth:180}}>
<label style={{fontSize:9,color:t.td}}>Title</label>
<input value={rmTitle} onChange={e=>setRmTitle(e.target.value)} autoFocus placeholder="What do you need to remember?" style={{...si,width:'100%'}} onKeyDown={e=>{if(e.key==='Enter'&&rmTitle.trim())saveReminder();}}/>
</div>
<div style={{flex:1,minWidth:140}}>
<label style={{fontSize:9,color:t.td}}>Date</label>
<DateInput value={rmDate} onChange={e=>setRmDate(e.target.value)} style={{...si,width:'100%'}}/>
</div>
<div style={{flex:1,minWidth:120}}>
<label style={{fontSize:9,color:t.td}}>Repeat</label>
<select value={rmRepeat} onChange={e=>setRmRepeat(e.target.value)} style={{...si,width:'100%'}}><option value="none">None</option><option value="weekly">Weekly</option><option value="monthly">Monthly</option><option value="yearly">Yearly</option></select>
</div>
<div style={{flex:2,minWidth:180}}>
<label style={{fontSize:9,color:t.td}}>Note (optional)</label>
<input value={rmNote} onChange={e=>setRmNote(e.target.value)} placeholder="Any details?" style={{...si,width:'100%'}}/>
</div>
<div style={{flex:1,minWidth:120,position:'relative'}}>
<label style={{fontSize:9,color:t.td}}>Vendor (optional)</label>
<input value={rmVendor} onChange={e=>{setRmVendor(e.target.value);setRmVendorOpen(true);}} onFocus={()=>setRmVendorOpen(true)} onBlur={()=>setTimeout(()=>setRmVendorOpen(false),150)} placeholder="Type to search..." style={{...si,width:'100%'}}/>
{rmVendorOpen&&rmVendor.length>=1&&(()=>{const matches=allVendors.map(v=>v.name).filter(v=>v.toLowerCase().includes(rmVendor.toLowerCase())).sort((a,b)=>a.localeCompare(b)).slice(0,5);return matches.length?<div style={{position:'absolute',top:'100%',left:0,right:0,zIndex:10,background:t.cd,border:`1px solid ${t.bd}`,borderRadius:6,marginTop:2,overflow:'hidden',boxShadow:'0 4px 8px rgba(0,0,0,0.1)'}}>
{matches.map(v=><div key={v} onMouseDown={e=>e.preventDefault()} onClick={()=>{setRmVendor(v);setRmVendorOpen(false);}} style={{padding:'6px 10px',fontSize:11,cursor:'pointer',borderBottom:`1px solid ${t.bd}`}} onMouseOver={e=>e.currentTarget.style.background=t.sf} onMouseOut={e=>e.currentTarget.style.background='transparent'}>{v}</div>)}
</div>:null;})()}
</div>
<button onClick={saveReminder} disabled={!rmTitle.trim()||!rmDate||rmSaving} style={{...sb,background:rmTitle.trim()&&rmDate?t.gn:'#888',opacity:rmTitle.trim()&&rmDate?1:0.5}}>{rmSaving?'Saving...':'Save'}</button>
</div>
</div>}

{view==='active'?<div style={{display:'flex',flexDirection:'column',gap:8,flex:1}}>
{/* Empty state */}
{pending.length===0&&completedToday.length===0&&<div style={{textAlign:'center',padding:'40px 16px'}}>
<div style={{fontSize:36,marginBottom:8}}>✓</div>
<div style={{fontSize:15,fontWeight:600,marginBottom:4}}>All clear!</div>
<div style={{fontSize:11,color:t.td}}>Tasks appear here when invoices arrive or reminders come due.</div>
</div>}

{/* Inbox zero celebration */}
{pending.length===0&&completedToday.length>0&&<div style={{textAlign:'center',padding:'24px 16px',background:t.sf,borderRadius:12}}>
<div style={{fontSize:36,marginBottom:4}}>🔥</div>
<div style={{fontSize:16,fontWeight:700,color:t.gn}}>Inbox Zero!</div>
<div style={{fontSize:11,color:t.tm,marginTop:2}}>{completedToday.length} cleared today{streak>0?` · ${streak} day streak`:''}</div>
</div>}

{/* Task cards — anomaly tasks flattened to one card per anomaly */}
{renderItems.map(({tk,anomaly,key})=>{
const isActive=activeTaskId===tk.id;
const isOD=overdue.includes(tk);
const snap=tk.payload?.invoice_snapshot;
const anomalyCount=tk.payload?.anomalies?.length||0;

if(anomaly){
const bs={fontSize:8,fontWeight:600,padding:'1px 5px',borderRadius:3};
let badge,detail;
if(anomaly.type==='price_change'){const up=anomaly.new_value>anomaly.old_value;badge=<span style={{...bs,background:up?'rgba(200,55,45,0.12)':'rgba(45,184,122,0.12)',color:up?t.ac:t.gn}}>Price {up?'↑':'↓'}</span>;detail=anomaly.old_value!=null&&anomaly.new_value!=null?`$${anomaly.old_value.toFixed(2)} → $${anomaly.new_value.toFixed(2)}${anomaly.pct_change!=null?` (${anomaly.pct_change>0?'+':''}${anomaly.pct_change}%)`:''}`:null;}
else if(anomaly.type==='quantity_change'){const up=anomaly.new_value>anomaly.old_value;badge=<span style={{...bs,background:'rgba(212,168,36,0.12)',color:t.yl}}>Qty {up?'↑':'↓'}</span>;detail=anomaly.old_value!=null&&anomaly.new_value!=null?`Qty: ${anomaly.old_value} → ${anomaly.new_value}`:null;}
else if(anomaly.type==='new_item'){badge=<span style={{...bs,background:'rgba(59,130,246,0.12)',color:t.bl}}>New</span>;detail=anomaly.new_value!=null?`$${anomaly.new_value.toFixed(2)} — not on prior invoices`:null;}
else if(anomaly.type==='missing_item'){badge=<span style={{...bs,background:'rgba(139,143,158,0.12)',color:t.td}}>Missing</span>;detail=anomaly.old_value!=null?`Was $${anomaly.old_value.toFixed(2)} on prior invoice`:null;}
else{badge=<span style={{...bs,background:'rgba(200,55,45,0.12)',color:t.ac}}>Math error</span>;detail='Qty × price doesn\'t match line total';}
return<div key={key} onClick={()=>{if(tk.payload?.invoice_id&&setInvoiceModalId){if(setInvoiceHighlightItem)setInvoiceHighlightItem(anomaly.item_number||null);setInvoiceModalId(tk.payload.invoice_id);}}} style={{background:t.sf,border:`0.5px solid ${t.bd}`,borderRadius:10,padding:12,cursor:'pointer'}}>
<div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:4}}>
<span style={{fontSize:10,textTransform:'uppercase',letterSpacing:0.4,color:t.ac,fontWeight:600}}>Anomaly</span>
<button onClick={e=>{e.stopPropagation();completeTask(tk.id);}} style={{fontSize:9,padding:'2px 8px',borderRadius:4,background:'transparent',border:`1px solid ${t.bd}`,cursor:'pointer',color:t.tm,fontFamily:"'DM Sans',sans-serif"}}>Acknowledge</button>
</div>
<div style={{fontSize:12,fontWeight:500,color:t.td,marginBottom:4}}>{tk.payload?.vendor||tk.title}</div>
<div style={{display:'flex',alignItems:'center',gap:6,fontSize:11}}>
{badge}<span style={{color:t.tm,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{anomaly.description||'—'}</span>
</div>
{detail&&<div style={{fontSize:10,color:t.td,marginTop:2,paddingLeft:2}}>{detail}</div>}
{isOD&&<div style={{fontSize:9,color:t.ac,fontWeight:600,marginTop:4}}>Overdue</div>}
</div>;}

return<div key={key} onClick={()=>{if(hasForm(tk))toggleForm(tk);else if(needsPrompt(tk)){setPromptTask(promptTask===tk.id?null:tk.id);setExpandedForm(null);}else if(tk.type==='manual_reminder'&&tk.payload?.vendor){setActiveTaskId(tk.id);}else if(isNavigable(tk)){if(tk.type==='invoice_review'){routeInvoiceReview(tk);}else{setActiveTaskId(tk.id);}}}} style={{background:t.sf,border:`0.5px solid ${isActive?t.bl:t.bd}`,borderRadius:10,padding:12,cursor:'pointer',animation:justCompleted===tk.id?'taskComplete 0.5s ease forwards':undefined}}>
{tk.type==='invoice_review'?<>
<div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:4}}>
<span style={{fontSize:10,textTransform:'uppercase',letterSpacing:0.4,color:t.ac,fontWeight:600}}>Invoice</span>
{anomalyCount>0&&<span style={{fontSize:9,padding:'1px 6px',borderRadius:4,background:'rgba(212,168,36,0.12)',color:t.yl,fontWeight:600}}>{anomalyCount} flag{anomalyCount!==1?'s':''}</span>}
</div>
<div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
<div style={{fontSize:13,fontWeight:500}}>{snap?.vendor||tk.title?.replace('Review: ','').split(' $')[0]||tk.title}</div>
<div style={{fontSize:13,fontWeight:500,fontFamily:mn}}>{snap?.grand_total?'$'+(snap.grand_total).toFixed(2):tk.title?.match(/\$[\d,.]+/)?.[0]||''}</div>
</div>
<div style={{fontSize:11,color:t.td,marginTop:2}}>{snap?.invoice_date||''}{snap?.vendor?' · ':''}{tk.note||''}</div>
</>
:tk.type==='cost_card_review'?<>
<div style={{fontSize:10,textTransform:'uppercase',letterSpacing:0.4,color:t.yl,fontWeight:600,marginBottom:4}}>Cost card</div>
<div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
<div style={{fontSize:13,fontWeight:500}}>{tk.payload?.cost_card_name||tk.title}</div>
<span style={{fontSize:12,color:t.td}}>›</span>
</div>
<div style={{fontSize:11,color:t.td,marginTop:2}}>{tk.note||'Food cost rose above target'}</div>
</>
:tk.type==='manual_reminder'?<>
<div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:4}}>
<span style={{fontSize:10,textTransform:'uppercase',letterSpacing:0.4,color:t.td,fontWeight:600}}>Reminder</span>
{!isNavigable(tk)&&!hasForm(tk)&&!tk.payload?.vendor&&<button onClick={e=>{e.stopPropagation();completeTask(tk.id);}} style={{fontSize:9,padding:'2px 8px',borderRadius:4,background:'transparent',border:`1px solid ${t.bd}`,cursor:'pointer',color:t.tm,fontFamily:"'DM Sans',sans-serif"}}>Done</button>}
</div>
<div style={{fontSize:13,fontWeight:500}}>{tk.title}</div>
{tk.payload?.vendor&&<div style={{fontSize:11,color:t.ac,marginTop:2}}>{tk.payload.vendor}</div>}
{(tk.due_at||tk.note)&&<div style={{fontSize:11,color:t.td,marginTop:2}}>{tk.due_at?new Date(tk.due_at).toLocaleDateString('en-US',{month:'short',day:'numeric',timeZone:'America/New_York'}):''}{tk.due_at&&tk.note?' · ':''}{tk.note||''}</div>}
</>
:<>
<div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:4}}>
<span style={{fontSize:10,textTransform:'uppercase',letterSpacing:0.4,color:t.gn,fontWeight:600}}>{tk.payload?.frequency||'Recurring'}</span>
{!isNavigable(tk)&&!hasForm(tk)&&<button onClick={e=>{e.stopPropagation();completeTask(tk.id);}} style={{fontSize:9,padding:'2px 8px',borderRadius:4,background:'transparent',border:`1px solid ${t.bd}`,cursor:'pointer',color:t.tm,fontFamily:"'DM Sans',sans-serif"}}>Done</button>}
{hasForm(tk)&&<span style={{fontSize:9,color:t.yl,fontWeight:500}}>{expandedForm===tk.id?'▾':'Fill form'}</span>}
{isNavigable(tk)&&!hasForm(tk)&&<span style={{fontSize:12,color:t.td}}>›</span>}
</div>
<div style={{fontSize:13,fontWeight:500}}>{tk.title}</div>
{tk.note&&<div style={{fontSize:11,color:t.td,marginTop:2}}>{tk.note}</div>}
</>}
{(()=>{if(!tk.due_at)return null;const taskDate=tk.due_at.split('T')[0];const today=new Date().toISOString().split('T')[0];if(taskDate<today)return<div style={{fontSize:11,color:t.ac,fontWeight:600,marginTop:4}}>For {parseLocalDate(taskDate).toLocaleDateString('en-US',{weekday:'short',month:'short',day:'numeric',timeZone:'America/New_York'})}</div>;return null;})()}
{isOD&&<div style={{fontSize:9,color:t.ac,fontWeight:600,marginTop:4}}>Overdue</div>}
{/* Inline expansions for form/prompt */}
{expandedForm===tk.id&&<div style={{background:t.cd,borderRadius:8,padding:12,marginTop:8}} onClick={e=>e.stopPropagation()}>
{tk.payload.form_fields.map((f,i)=><div key={i} style={{marginBottom:8}}>
<label style={{fontSize:9,color:formErrors[i]?t.ac:t.td,textTransform:'uppercase'}}>{f.label}{f.required?' *':''}</label>
<input type={f.type==='number'?'number':f.type==='date'?'date':'text'} value={formValues[i]||''} onChange={e=>setFormValues(p=>({...p,[i]:e.target.value}))} style={{...si,width:'100%',boxSizing:'border-box',borderColor:formErrors[i]?t.ac:undefined}} onFocus={()=>setFormErrors(p=>{const n={...p};delete n[i];return n;})}/>
</div>)}
<div style={{display:'flex',gap:6,justifyContent:'flex-end'}}>
<button onClick={()=>setExpandedForm(null)} style={{...sb,background:t.sf,color:t.tm,border:`1px solid ${t.bd}`,fontSize:10}}>Cancel</button>
<button onClick={()=>submitForm(tk)} disabled={formSaving} style={{...sb,background:t.gn,fontSize:10}}>{formSaving?'Saving...':'Submit'}</button>
</div>
</div>}
{promptTask===tk.id&&<div style={{background:t.cd,borderRadius:8,padding:10,marginTop:8,display:'flex',alignItems:'center',justifyContent:'space-between',gap:8}} onClick={e=>e.stopPropagation()}>
<div style={{fontSize:11,color:t.tm}}>{PROMPT_TEXT[tk.target_page]||'Done?'}</div>
<div style={{display:'flex',gap:6,flexShrink:0}}>
<button onClick={()=>{setPromptTask(null);if(tk.target_page&&setPg){setReviewTaskId(tk.id);setPg(tk.target_page);}}} style={{...sb,background:t.sf,color:t.tm,border:`1px solid ${t.bd}`,fontSize:10}}>Not yet</button>
<button onClick={()=>{setPromptTask(null);completeTask(tk.id);}} style={{...sb,background:t.gn,fontSize:10}}>Yes</button>
</div>
</div>}
</div>;})}
</div>:
<div style={{textAlign:'center',padding:'40px 16px',color:t.td,fontSize:12}}>Browse completed tasks in the left pane.</div>}
</div>
</div>);
}
